import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Modal, Select, toast } from '@hrms/ui';
import { canAnywhere } from '@hrms/shared';
import { ME_QUERY_KEY, tsr } from '../../lib/api.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { errorMessage } from '../recruitment/errorMessage.js';
import { COMPANY_GROUP_QUERY_KEY } from './useCompanyGroup.js';

function dismissKey(orgId: string, personId: string) {
  return `peopleorbit_link_account_dismissed_${orgId}_${personId}`;
}

function isDismissed(orgId: string, personId: string): boolean {
  try {
    return localStorage.getItem(dismissKey(orgId, personId)) === '1';
  } catch {
    return true;
  }
}

/**
 * Offered to an admin whose company was signed up on the public page with an email that already
 * signs in to another company (docs/adr/0034-company-groups.md §5): "link them, use one password,
 * switch between them". Nothing links without that other login's password. Renders nothing until
 * the API finds a candidate — which it only does once this account's email is verified.
 */
export function LinkAccountBanner() {
  const { actor } = useAuthenticatedSession();
  const allowed = canAnywhere(actor, 'org.company:create');
  const [dismissed, setDismissed] = useState(() => isDismissed(actor.orgId, actor.personId));
  const [open, setOpen] = useState(false);
  const candidates = tsr.companyGroup.listLinkCandidates.useQuery({
    queryKey: ['company-group', 'link-candidates'],
    enabled: allowed && !dismissed,
    staleTime: 10 * 60_000,
  });
  const list = candidates.data?.status === 200 ? candidates.data.body.candidates : [];
  if (!allowed || dismissed || list.length === 0) return null;

  const names = list.map((c) => c.orgName).join(', ');
  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-info/30 bg-info-subtle px-4 py-2 text-sm text-info sm:px-6"
    >
      <span>
        Your email also signs in to {names}. Link them to use one password and switch between
        companies.
      </span>
      <span className="flex gap-3">
        <button
          type="button"
          className="font-medium underline hover:no-underline"
          onClick={() => setOpen(true)}
        >
          Link accounts
        </button>
        <button
          type="button"
          className="text-info/80 hover:text-info"
          onClick={() => {
            try {
              localStorage.setItem(dismissKey(actor.orgId, actor.personId), '1');
            } catch {
              // Storage unavailable: the banner simply comes back next time.
            }
            setDismissed(true);
          }}
        >
          Not now
        </button>
      </span>
      <LinkAccountDialog open={open} candidates={list} onClose={() => setOpen(false)} />
    </div>
  );
}

function LinkAccountDialog({
  open,
  candidates,
  onClose,
}: {
  open: boolean;
  candidates: { orgId: string; orgName: string; mfaRequired: boolean; groupName: string | null }[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [orgId, setOrgId] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [joinGroup, setJoinGroup] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const link = tsr.companyGroup.linkAccount.useMutation();

  useEffect(() => {
    if (!open) return;
    setOrgId(candidates[0]?.orgId ?? '');
    setPassword('');
    setCode('');
    setJoinGroup(true);
    setError(null);
  }, [open, candidates]);

  const candidate = candidates.find((c) => c.orgId === orgId);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!candidate || !password) {
      setError(`Enter your password for ${candidate?.orgName ?? 'that company'}.`);
      return;
    }
    if (candidate.mfaRequired && !/^\d{6}$/.test(code.trim())) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    link.mutate(
      {
        body: {
          orgId: candidate.orgId,
          password,
          ...(candidate.mfaRequired ? { totpCode: code.trim() } : {}),
          joinGroup,
        },
      },
      {
        onSuccess: ({ body }) => {
          toast.success(
            `Linked to ${body.linkedTo.orgName}. Sign in with your ${body.linkedTo.orgName} password from now on.`,
          );
          // /auth/me says whether there is anything to switch to; the menu waits on it.
          void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: ['auth', 'linked-organizations'] });
          void queryClient.invalidateQueries({ queryKey: COMPANY_GROUP_QUERY_KEY });
          onClose();
        },
        onError: (mutationError) => setError(errorMessage(mutationError)),
      },
    );
  }

  return (
    <Modal
      open={open}
      title="Link your accounts"
      onClose={onClose}
      sheetOnPhone
      showCloseButton
      trapFocus
    >
      <form className="flex flex-col gap-4 text-fg" onSubmit={handleSubmit} noValidate>
        <p className="text-sm text-fg-muted">
          Prove it is you with the password of the other company. After linking, you sign in to this
          company with that password too, and switch between them from the top bar.
        </p>
        {candidates.length > 1 && (
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-fg">Company</span>
            <Select value={orgId} onChange={(event) => setOrgId(event.target.value)}>
              {candidates.map((c) => (
                <option key={c.orgId} value={c.orgId}>
                  {c.orgName}
                </option>
              ))}
            </Select>
          </label>
        )}
        <Input
          label={`Your password for ${candidate?.orgName ?? 'the other company'}`}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {candidate?.mfaRequired && (
          <Input
            label="2-step code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        )}
        {candidate?.groupName && (
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 cursor-pointer accent-accent"
              checked={joinGroup}
              onChange={(event) => setJoinGroup(event.target.checked)}
            />
            <span className="text-fg-muted">Also add this company to {candidate.groupName}</span>
          </label>
        )}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" onClick={onClose} disabled={link.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={link.isPending}>
            Link accounts
          </Button>
        </div>
      </form>
    </Modal>
  );
}
